import React, {
  useState,
  useEffect,
  CSSProperties,
  forwardRef,
  useImperativeHandle,
  useRef,
  useMemo,
  useCallback,
} from "react";
import useGetState from "@/hooks/useGetState";
import LoadingGif from "@/components/LoadingGif";
import { Checkbox, Spin } from "@arco-design/web-react";
import "./index.less";
import { debounce } from "@/utils";
import emptyImg from "@/assets/images/empty.png";
import useLatestEffect from "@/hooks/useLastEffect";

export interface IApiConfig {
  page?: string;
  limit?: string;
  items?: string;
  total?: string;
}

export interface ApiScrollListRef {
  loadData: (isRefresh?: boolean) => void;
  clearData: () => void;
}

interface SelectOptions {
  key?: string;
  type?: "checkbox" | undefined;
  multiple?: boolean;
  returnObject?: boolean;
}

export interface ApiScrollListProps {
  // key?: string | number; // select模式下选择的值
  className?: string;
  style?: CSSProperties;
  listStyle?: CSSProperties;
  itemStyle?: CSSProperties;
  height?: string;
  beforeRequest?: (params: any) => boolean | Promise<boolean>;
  selectable?: boolean;
  selectValue?: any[];
  onSelectChange?: (ids: any[], data: any) => void;
  selectOptions?: SelectOptions;
  api: (params: any, refreshList: any[]) => Promise<any>;
  apiConfig?: IApiConfig;
  pagination?: boolean;
  params?: Record<string, any>;
  renderItem?: (
    data: any,
    index: number,
    list: any[],
    response: any
  ) => React.ReactNode;
  renderList?: (list: any[]) => React.ReactNode;
  renderEmpty?: React.ReactNode;
  onLoaded?: (list: any[], response: any) => void;
  children?: React.ReactNode;
}

const ApiScrollList = forwardRef(
  (props: ApiScrollListProps, ref: React.ForwardedRef<ApiScrollListRef>) => {
    const observer = useRef<IntersectionObserver>();
    const isFirstRender = useRef(true);
    const scrollListRef = useRef<any>(null);
    const lastChild = useRef();
    const [response, setResponse] = useState<any>({});
    const [refreshList, setRefreshList] = useState<any[]>([]);
    const [hasMore, sethasMore] = useState(true);
    const requestId = useRef(0);
    const [checkList, setCheckList, getCheckList] = useGetState<any[]>([]);
    const [pager, setPager, getPager] = useGetState<Record<string, any>>({
      page: 1,
      limit: 10,
    });
    const [cursor, setCursor, getCursor] = useGetState(0);
    const [loading, setLoading] = useState(false);

    const {
      apiConfig,
      className,
      style = {},
      listStyle = {},
      itemStyle = {},
      onLoaded,
      renderEmpty,
      beforeRequest = () => true,
      height = "100%",
      api,
      params,
      renderItem,
      selectValue,
      selectable = false,
      selectOptions = {
        key: "id",
        multiple: false,
        returnObject: false,
      },
      pagination = true,
      onSelectChange,
    } = props;

    const selectOpt = useMemo(() => {
      return {
        selectable: selectable ?? false,
        type: selectOptions.type ?? null,
        key: selectOptions.key ?? "id",
        multiple: selectOptions.multiple ?? false,
      };
    }, [selectOptions]);

    const checkIds = useMemo(() => {
      return checkList.map((item) => item[selectOpt.key]);
    }, [checkList, selectOpt.key]);

    const getResponseList = (data, responseKey) => {
      console.log("getRes", data, responseKey);

      for (const key of responseKey) {
        data = key ? data[key] : data;
      }
      return data;
    };

    const fetchApi = (queryParams, isRefresh) => {
      requestId.current = Date.now();
      const fetchId = requestId.current;

      return new Promise((resolve) => {
        props.api(queryParams, isRefresh ? [] : refreshList).then((res) => {
          console.log("fetchId", fetchId, requestId.current, queryParams);
          console.log("aaa", res);
          if (fetchId !== requestId.current) {
            return Promise.reject("canceled");
          }
          resolve(res);
        });
      });
    };

    const loadData = async (isRefresh = false, clearWhenRefresh = true) => {
      console.log("loading=======", loading);
      if (loading && !isRefresh) return;
      try {
        const defaultApiConfig = {
          page: "page",
          limit: "page_size",
          items: "data",
          total: "total",
        };

        const mergeApiConfig = { ...defaultApiConfig, ...apiConfig };
        // console.log("mergeApiConfig", mergeApiConfig, cursor);

        if (isRefresh) {
          setLoading(true);
          sethasMore(true);
          setPager({ page: 1, limit: 10 });
          setCursor(0);
          if (clearWhenRefresh) setRefreshList([]);
        } else {
          if (!hasMore) return;
          setLoading(true);
          setPager((prevS) => ({ ...prevS, page: prevS.page + 1 }));
        }

        const queryParams = {
          [mergeApiConfig.page]:
            mergeApiConfig.page === "cursor" ? getCursor() : getPager().page,
          [mergeApiConfig.limit]: getPager().limit,
          ...props.params,
        };

        const shouldRequest = await beforeRequest?.(props.params);

        if (!shouldRequest) {
          setLoading(false);
          sethasMore(true);
          setCursor(0);
          return;
        }

        const res: any = await fetchApi(
          queryParams,
          isRefresh ? [] : refreshList
        );
        console.log("bbb", res);

        // const { data, total } = res;
        const responseKey = mergeApiConfig.items.split(".");
        const newList = getResponseList(res, responseKey);

        const data = (Array.isArray(newList) ? newList : []) ?? [];
        const total = res?.[mergeApiConfig.total] ?? 0;

        // cursor滚动加载
        if (mergeApiConfig.page === "cursor") {
          // console.log("apiConfig", apiConfig);
          setCursor(res.nextCusor ?? 0);
        }

        const resultList = isRefresh ? data : [...refreshList, ...data];
        if (
          (mergeApiConfig.page !== "cursor" &&
            (data.length < pager.limit ||
              pagination === false ||
              resultList.length >= total)) ||
          (mergeApiConfig.page === "cursor" && !res.nextCusor)
        ) {
          // 加载结束
          sethasMore(false);
        } else {
          sethasMore(true);
        }

        if (isRefresh) {
          setRefreshList(data);
        } else {
          setRefreshList([...refreshList, ...data]);
        }
        setLoading(false);
        setResponse(res);
        // console.log("resultList", resultList);
        onLoaded?.(resultList, res);
      } catch (e) {
        console.log("ApiScrollList Load Data Error:", e);
        setLoading(false);
      }
    };

    useImperativeHandle(ref, () => ({
      loadData: loadData,
      clearData: () => {
        sethasMore(true);
        setRefreshList([]);
      },
      changeList: (list) => {
        setRefreshList(list);
      },
      updateItem(item) {
        const index = refreshList.findIndex(
          (i) => i[selectOpt.key] === item[selectOpt.key]
        );
        if (index !== -1) {
          setRefreshList((list) => list.splice(index, 1, item));
        }
      },
    }));

    const commit = () => {
      console.log("commit-------------", getCheckList(), checkIds);
      onSelectChange?.(
        getCheckList().map((item) => item[selectOpt.key]),
        getCheckList()
      );
    };

    useLatestEffect(() => {
      console.log("api change --------", params);
      loadData(true);
    }, [api, params]);

    useEffect(() => {
      if (!pagination) return;
      console.log("lastChild", lastChild.current);
      observer.current = new IntersectionObserver(
        debounce((entries) => {
          if (entries[0].isIntersecting) {
            console.log("最后一个元素进入视口！");
            if (refreshList.length) loadData();
          }
        }, 300),
        { threshold: 0.1 }
      );

      if (lastChild.current) {
        observer.current.observe(lastChild.current);
      }

      return () => {
        if (lastChild.current) {
          observer.current.disconnect();
        }
      };
    }, [lastChild.current, hasMore, loading]);

    useEffect(() => {
      if (!selectValue?.length && refreshList.length) {
        setCheckList([]);
        return;
      }

      if (selectValue?.length && refreshList.length) {
        setCheckList(
          refreshList.filter((item) => {
            const id = item[selectOpt.key];
            return selectValue.includes(id);
          })
        );
      }
    }, [selectValue, selectOpt.key, refreshList]);

    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          height: height,
          ...style,
        }}
      >
        <div
          ref={scrollListRef}
          className={className}
          style={{
            // position: "relative",
            height: "100%",
            overflowY: "auto",
            alignContent: "flex-start",
            ...listStyle,
          }}
        >
          {renderItem &&
            refreshList.map((item, index) => {
              return (
                <div
                  key={index}
                  className={`api-scroll-list-item ${checkIds.includes(item[selectOpt.key]) ? "active" : ""} ${selectOpt.type === "checkbox" ? "is-checkbox" : ""}`}
                  style={{
                    position: "relative",
                    overflow: "hidden",
                    ...itemStyle,
                  }}
                  onClick={() => {
                    if (
                      selectOpt.type === "checkbox" ||
                      !selectOpt.selectable
                    ) {
                      return;
                    }
                    const id = item[selectOpt.key];
                    if (selectOpt.multiple) {
                      if (checkIds.includes(id)) {
                        setCheckList(
                          checkList.filter((i) => i[selectOpt.key] !== id)
                        );
                      } else {
                        setCheckList([...checkList, item]);
                      }
                    } else {
                      setCheckList([item]);
                    }

                    commit();
                  }}
                >
                  {selectOpt.type === "checkbox" && (
                    <div
                      style={{
                        position: "absolute",
                        zIndex: 9,
                        right: 8,
                        top: 6,
                      }}
                    >
                      <Checkbox
                        checked={checkIds.includes(item[selectOpt.key])}
                        onChange={(checked) => {
                          if (checked) {
                            if (selectOpt.multiple) {
                              setCheckList([...checkList, item]);
                            } else {
                              setCheckList([item]);
                            }
                          } else {
                            if (selectOpt.multiple) {
                              setCheckList(
                                checkList.filter(
                                  (i) =>
                                    i[selectOpt.key] !== item[selectOpt.key]
                                )
                              );
                            } else {
                              setCheckList([]);
                            }
                          }

                          commit();
                        }}
                      />
                    </div>
                  )}
                  {props.renderItem(item, index, refreshList, response)}
                </div>
              );
            })}
          {props.renderList && props.renderList(refreshList)}

          {/* {pagination !== false && !hasMore && !!refreshList.length && (
            <div
              className="end-text"
              style={{
                width: "100%",
                padding: "16px 0",
                fontSize: 12,
                textAlign: "center",
              }}
            >
              我是有底线的~
            </div>
          )} */}

          {!hasMore && !refreshList.length && !renderEmpty && (
            <div
              className="empty-text"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "absolute",
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                fontSize: 14,
              }}
            >
              <img src={emptyImg} style={{ width: "60px" }} />
              <div style={{ marginTop: 8 }}>暂无数据</div>
            </div>
          )}

          {!hasMore && !refreshList.length && renderEmpty}

          <div ref={lastChild} className="sentinel" style={{ height: 1 }}></div>
        </div>
        {loading && (
          <Spin
            style={{
              position: "absolute",
              zIndex: 2,
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          ></Spin>
        )}
      </div>
    );
  }
);
export default React.memo(ApiScrollList);
